/**
 * Utilities for getting parent frame info when app runs inside an iframe.
 * Used to obtain parent origin as base URL for task-owner/task-assignee APIs.
 */

/**
 * Whether the app is running inside an iframe.
 */
export function isInIframe(): boolean {
  try {
    return typeof window !== "undefined" && window.self !== window.top;
  } catch {
    return true; // Access denied (cross-origin) implies iframe
  }
}

/**
 * Get parent frame base URL (origin) for use as API prefix.
 * Priority:
 * 1. URL param `parentBaseUrl` (parent can pass when embedding)
 * 2. document.referrer origin (when in iframe, referrer is parent URL)
 * 3. window.parent.location.origin (same-origin only)
 * 4. Empty string (caller should fallback to apiClient base)
 */
export function getParentBaseUrl(): string {
  try {
    // 1. URL param override (parent embeds with ?parentBaseUrl=https://...)
    const params = new URLSearchParams(window.location.search);
    const fromParam = params.get("parentBaseUrl");
    if (fromParam) {
      const url = new URL(fromParam);
      return url.origin.replace(/\/$/, "");
    }

    // 2. document.referrer (works in cross-origin iframe when not stripped)
    if (isInIframe() && document.referrer) {
      const url = new URL(document.referrer);
      return url.origin.replace(/\/$/, "");
    }

    // 3. Same-origin: window.parent.location.origin
    if (window.self !== window.top && window.parent?.location?.origin) {
      return window.parent.location.origin.replace(/\/$/, "");
    }
  } catch {
    // Cross-origin access or invalid URL
  }
  return "";
}
